/**
 * Allocate BOM consumption across scanned source bags.
 *
 * For each BOM line, `needed = outputQty × per-unit quantity` is taken
 * greedily from the matching bags in scan order — the first bag gives up
 * to its remaining quantity, the next covers the rest — so two bags of
 * the same item never both get charged the full amount. Bags matching no
 * line get no allocation (their consumption is untracked by quantity).
 * Each bag's availability is tracked across lines, so even malformed
 * BOMs with duplicate lines can never over-allocate a bag.
 *
 * Pure and deterministic: the assemble page runs it for the live
 * needed/supplied/short display, and the assemble transaction re-runs it
 * against fresh source reads so the recorded decrements match reality.
 *
 * @param {number} outputQty - Units being assembled.
 * @param {Array<{projectDocId: string, pPartDocId: string, quantity: number}>} components
 *   The output part's BOM lines.
 * @param {Array<{id: string, projectDocId: string, pPartDocId: string, remainingQty: number}>} sources
 *   Scanned source bags, in scan order.
 * @returns {{
 *   lines: Array<{projectDocId: string, pPartDocId: string, quantity: number,
 *     needed: number, supplied: number, short: number}>,
 *   deltaByLotId: Object<string, number>,
 * }} Per-line coverage plus each bag's (negative) quantity delta.
 */
// eslint-disable-next-line import/prefer-default-export
export function allocateBomConsumption(outputQty, components, sources) {
  const deltaByLotId = {};
  const availableByLotId = {};
  sources.forEach((source) => {
    availableByLotId[source.id] = Math.max(source.remainingQty ?? 0, 0);
  });

  const lines = components.map((component) => {
    const needed = outputQty * component.quantity;
    let remainingNeed = needed;
    let supplied = 0;

    sources.forEach((source) => {
      if (source.projectDocId !== component.projectDocId
        || source.pPartDocId !== component.pPartDocId) return;
      const available = availableByLotId[source.id];
      supplied += available;
      const take = Math.min(available, remainingNeed);
      remainingNeed -= take;
      availableByLotId[source.id] -= take;
      if (take > 0) {
        deltaByLotId[source.id] = (deltaByLotId[source.id] || 0) - take;
      }
    });

    return {
      projectDocId: component.projectDocId,
      pPartDocId: component.pPartDocId,
      quantity: component.quantity,
      needed,
      supplied,
      short: remainingNeed,
    };
  });

  return { lines, deltaByLotId };
}
